import React from "react";
import { motion, useReducedMotion } from "framer-motion";

// Neon chomper that runs along a row of dots and eats the ghosts one by one, then the loop restarts.
const DURATION = 9;
const RUN_END = 0.82; // share of the loop spent running; the rest is the pause before ghosts come back
const START = -16; // chomper left edge, % of the track
const END = 104;
const BITE = 7; // how far past a ghost's left edge the chomper's left edge is when it bites

const GHOSTS = [
  { left: 24, color: "#22d3ee" },
  { left: 44, color: "#f9a8d4" },
  { left: 64, color: "#ef4444" },
  { left: 84, color: "#fb923c" },
];
const DOTS = [14, 19, 34, 39, 54, 59, 74, 79, 94];

const FRIGHTENED = "#60a5fa";

// Loop time (0..1) when the chomper's left edge reaches a given % of the track.
const timeAt = (pos) => Math.min(Math.max(((pos - START) / (END - START)) * RUN_END, 0.001), RUN_END);

const glow = (color) => ({
  color,
  filter: `drop-shadow(0 0 4px ${color}) drop-shadow(0 0 14px ${color})`,
});

const MOUTH_OPEN = "M50 50 L92 26 A48 48 0 1 0 92 74 Z";
const MOUTH_SHUT = "M50 50 L98 47 A48 48 0 1 0 98 53 Z";

const Chomper = ({ animate }) => (
  <svg viewBox="0 0 100 100" className="w-full h-auto overflow-visible" style={glow("#facc15")}>
    {/* Native SVG morph: framer-motion can't interpolate the arc command reliably. */}
    <path d={MOUTH_OPEN} fill="rgba(250,204,21,0.12)" stroke="currentColor" strokeWidth="7" strokeLinejoin="round">
      {animate && (
        <animate attributeName="d" values={`${MOUTH_OPEN};${MOUTH_SHUT};${MOUTH_OPEN}`} dur="0.32s" repeatCount="indefinite" />
      )}
    </path>
  </svg>
);

const Ghost = ({ color }) => (
  <svg viewBox="0 0 100 100" className="w-full h-auto overflow-visible">
    <path
      d="M10 92 V46 A40 40 0 0 1 90 46 V92 Q80 80 70 92 Q60 104 50 92 Q40 80 30 92 Q20 104 10 92 Z"
      fill="currentColor"
      fillOpacity="0.12"
      stroke="currentColor"
      strokeWidth="7"
      strokeLinejoin="round"
    />
    <ellipse cx="36" cy="50" rx="9" ry="12" fill="#e4e4e7" />
    <ellipse cx="64" cy="50" rx="9" ry="12" fill="#e4e4e7" />
    <circle cx="39" cy="53" r="4.5" fill="#0a0a0a" />
    <circle cx="67" cy="53" r="4.5" fill="#0a0a0a" />
  </svg>
);

const NeonChomp = ({ className = "" }) => {
  const reduceMotion = useReducedMotion();
  const loop = { duration: DURATION, repeat: Infinity, ease: "linear" };

  return (
    <div className={`relative w-full aspect-[5/2] overflow-hidden ${className}`} dir="ltr" aria-hidden="true">
      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2">
        {/* Pellets */}
        {DOTS.map((pos) => {
          const t = timeAt(pos - BITE - 2);
          return (
            <motion.span
              key={pos}
              className="absolute top-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-amber-100"
              style={{ left: `${pos}%`, boxShadow: "0 0 8px #fde68a" }}
              animate={reduceMotion ? undefined : { opacity: [1, 1, 0, 0, 1] }}
              transition={{ ...loop, times: [0, t, t + 0.005, 0.93, 1] }}
            />
          );
        })}

        {/* Ghosts: turn frightened blue just before the bite, then vanish */}
        {GHOSTS.map((g) => {
          const t = timeAt(g.left - BITE);
          const warn = Math.max(t - 0.08, 0.001);
          return (
            <motion.div
              key={g.left}
              className="absolute top-1/2 w-[13%]"
              style={{ left: `${g.left}%`, y: "-50%", ...glow(g.color) }}
              animate={
                reduceMotion
                  ? undefined
                  : {
                      color: [g.color, g.color, FRIGHTENED, FRIGHTENED, g.color, g.color],
                      filter: [
                        glow(g.color).filter,
                        glow(g.color).filter,
                        glow(FRIGHTENED).filter,
                        glow(FRIGHTENED).filter,
                        glow(g.color).filter,
                        glow(g.color).filter,
                      ],
                      opacity: [1, 1, 1, 0, 0, 1],
                      scale: [1, 1, 1, 0.3, 0.3, 1],
                    }
              }
              transition={{ ...loop, times: [0, warn, t, t + 0.02, 0.93, 1] }}
            >
              <motion.div
                animate={reduceMotion ? undefined : { y: ["0%", "-6%", "0%"] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut", delay: g.left / 60 }}
              >
                <Ghost color={g.color} />
              </motion.div>
            </motion.div>
          );
        })}

        {/* Chomper */}
        <motion.div
          className="absolute top-1/2 w-[14%]"
          style={{ left: reduceMotion ? "4%" : `${START}%`, y: "-50%" }}
          animate={reduceMotion ? undefined : { left: [`${START}%`, `${END}%`, `${END}%`], opacity: [1, 1, 0] }}
          transition={{ ...loop, times: [0, RUN_END, RUN_END + 0.01] }}
        >
          <Chomper animate={!reduceMotion} />
        </motion.div>
      </div>
    </div>
  );
};

export default NeonChomp;
